import React from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@/components/ui/button";
import { useAppNavigate } from "@/lib/AppNavigatorHook";
import { CHECKOUT_PRIMARY_CTA_CLASS } from "./components/checkoutPaymentLabels";

interface CheckoutSuccessLocationState {
  orderId?: string;
  packageId?: string;
  packageName?: string;
}

function truncateOrderId(orderId: string, visible = 8): string {
  if (orderId.length <= visible + 4) return orderId;
  return `${orderId.slice(0, visible)}…${orderId.slice(-4)}`;
}

export const CheckoutSuccessPage: React.FC = () => {
  const location = useLocation();
  const { goToPackageModules, goToDashboard } = useAppNavigate();

  const state = (location.state ?? {}) as CheckoutSuccessLocationState;
  const { orderId, packageId, packageName } = state;

  const handleAccessPackage = () => {
    if (packageId) {
      goToPackageModules(packageId, { name: packageName });
    } else {
      goToDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      {/* Keyframes duplicated in PaymentProcessingOverlay.tsx — keep in sync */}
      <style>{`
        @keyframes ofc-success-pop {
          0% { transform: scale(0.85); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes ofc-success-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        @keyframes ofc-success-float {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-6px); opacity: 0.7; }
        }
        .ofc-success-icon {
          animation: ofc-success-pop 0.5s ease-out forwards;
        }
        .ofc-success-ring {
          animation: ofc-success-ring 1.2s ease-out 0.2s forwards;
        }
        .ofc-success-sparkle {
          animation: ofc-success-float 2.5s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .ofc-success-icon,
          .ofc-success-ring,
          .ofc-success-sparkle {
            animation: none;
          }
        }
      `}</style>

      <div className="max-w-lg w-full">
        <div className="bg-background rounded-2xl border border p-8 md:p-10 text-center relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-30"
            aria-hidden="true"
          >
            <span className="ofc-success-sparkle absolute top-8 left-10 h-2 w-2 rounded-full bg-success" />
            <span
              className="ofc-success-sparkle absolute top-14 right-12 h-1.5 w-1.5 rounded-full bg-success-subtle"
              style={{ animationDelay: "0.4s" }}
            />
            <span
              className="ofc-success-sparkle absolute bottom-20 left-16 h-1.5 w-1.5 rounded-full bg-success"
              style={{ animationDelay: "0.8s" }}
            />
          </div>

          <div className="mb-6 flex justify-center relative">
            <span
              className="ofc-success-ring absolute h-20 w-20 rounded-full border-2 border-success"
              aria-hidden="true"
            />
            <div className="ofc-success-icon bg-success-muted rounded-full p-5 relative">
              <FontAwesomeIcon icon={faCheckCircle} className="text-5xl text-success" />
            </div>
          </div>

          <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            Compra confirmada!
          </h1>

          {packageName ? (
            <p className="text-base text-text-secondary mb-2">
              <span className="font-medium text-foreground">{packageName}</span> já está disponível na
              sua conta.
            </p>
          ) : (
            <p className="text-base text-text-secondary mb-2">
              Seu pedido foi confirmado. O acesso ao conteúdo será liberado em instantes.
            </p>
          )}

            <p className="text-sm text-success-subtle font-medium mb-1">
            Quando quiser, abra o primeiro módulo e comece.
          </p>
          <p className="text-sm text-text-tertiary mb-6">
            Enviamos um e-mail com os detalhes da compra.
          </p>

          {orderId ? (
            <div className="bg-surface rounded-lg px-4 py-3 mb-6 text-left">
              <p className="text-xs text-text-tertiary mb-1">Número do pedido</p>
              <p
                className="text-sm font-mono text-surface-foreground"
                title={orderId}
              >
                {truncateOrderId(orderId)}
              </p>
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handleAccessPackage}
              className={`${CHECKOUT_PRIMARY_CTA_CLASS} px-6 py-3 font-semibold min-h-[44px]`}
            >
              {packageId ? "Abrir o primeiro módulo" : "Ir para módulos"}
            </Button>
            <Button
              variant="outline"
              onClick={() => goToDashboard()}
              className="px-6 py-3 min-h-[44px]"
            >
              Ver todos os módulos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
